import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader } from 'lucide-react';
import doctorService from '../../services/doctorService';
import { formatDoctorName } from '../../utils/doctorName';
import {
  addDaysToDateStr,
  dateStrAtNoon,
  getTodayInBogota,
  getWeekStartInBogota,
  formatSlotLabel,
} from '../../utils/appointmentSchedule';

// Médicos que se muestran por celda antes de plegar el resto en "+N".
const MAX_VISIBLE_PER_CELL = 3;

const formatDayHeader = (dateStr) => ({
  weekday: dateStrAtNoon(dateStr).toLocaleDateString('es-CO', { weekday: 'short', timeZone: 'UTC' }),
  day: dateStrAtNoon(dateStr).getUTCDate(),
});

// Para lectores de pantalla: 'jueves, 9 de octubre'.
const formatDayLong = (dateStr) => dateStrAtNoon(dateStr).toLocaleDateString('es-CO', {
  weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
});

const formatRange = (dates) => {
  if (dates.length === 0) return '';
  const opts = { day: 'numeric', month: 'short', timeZone: 'UTC' };
  const first = dateStrAtNoon(dates[0]).toLocaleDateString('es-CO', opts);
  const last = dateStrAtNoon(dates[dates.length - 1]).toLocaleDateString('es-CO', { ...opts, year: 'numeric' });
  return `${first} – ${last}`;
};

// Versión corta para que quepa en la celda: solo el primer apellido.
const shortDoctorName = (doctor) => {
  const { firstName = '', lastName = '' } = doctor?.user?.profile || {};
  return formatDoctorName({ user: { profile: { firstName, lastName: lastName.split(' ')[0] } } });
};

/**
 * Calendario semanal con los horarios libres de todos los médicos.
 *
 * Cada celda (día × hora) lista los médicos que tienen ese slot libre; al
 * pulsar uno se llama a `onSelectSlot(doctor, fecha, hora)` para abrir el mismo
 * modal de agendar que usan las tarjetas, ya con el hueco elegido. Cambiar
 * `refreshKey` vuelve a pedir la semana (p. ej. tras un intento de reserva).
 */
function WeeklyAvailabilityCalendar({ doctors, onSelectSlot, refreshKey = 0 }) {
  const currentWeekStart = getWeekStartInBogota();
  const [weekStart, setWeekStart] = useState(currentWeekStart);
  const [dates, setDates] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [expandedCell, setExpandedCell] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setExpandedCell(null);

    (async () => {
      try {
        const res = await doctorService.getWeeklySlots(weekStart);
        if (cancelled) return;
        setDates(res?.dates || []);
        setAvailability(res?.availability || {});
      } catch (err) {
        if (cancelled) return;
        console.error('❌ Error cargando disponibilidad semanal:', err);
        setDates([]);
        setAvailability({});
        setError('No se pudo cargar la disponibilidad de esta semana.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [weekStart, refreshKey]);

  const specialties = useMemo(
    () => [...new Set(doctors.map((d) => d.specialization).filter(Boolean))].sort(),
    [doctors]
  );

  // Solo médicos que siguen en la lista de tarjetas: si el backend devolviera
  // uno que no está ahí, no habría con qué abrir el modal.
  const visibleDoctors = useMemo(
    () => doctors.filter((d) => availability[d._id] && (!specialty || d.specialization === specialty)),
    [doctors, availability, specialty]
  );

  // Filas: la unión de horas libres de los médicos visibles en la semana.
  const times = useMemo(() => {
    const all = new Set();
    visibleDoctors.forEach((d) => {
      Object.values(availability[d._id]).forEach((slots) => slots.forEach((t) => all.add(t)));
    });
    return [...all].sort();
  }, [visibleDoctors, availability]);

  const doctorsAt = (date, time) =>
    visibleDoctors.filter((d) => availability[d._id][date]?.includes(time));

  const today = getTodayInBogota();
  const canGoBack = weekStart > currentWeekStart;

  return (
    <section className="bg-white rounded-lg shadow-md p-4 sm:p-6" aria-label="Disponibilidad semanal">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Disponibilidad semanal</h3>
          <p className="text-sm text-gray-500">
            Elige un horario y un médico (hora Colombia, GMT-5)
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {specialties.length > 1 && (
            <select
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              aria-label="Filtrar por especialidad"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-600"
            >
              <option value="">Todas las especialidades</option>
              {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <button
            type="button"
            onClick={() => setWeekStart(addDaysToDateStr(weekStart, -7))}
            disabled={!canGoBack || loading}
            aria-label="Semana anterior"
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-gray-700 min-w-[9rem] text-center">
            {formatRange(dates)}
          </span>
          <button
            type="button"
            onClick={() => setWeekStart(addDaysToDateStr(weekStart, 7))}
            disabled={loading}
            aria-label="Semana siguiente"
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 text-gray-600 py-12">
          <Loader className="h-5 w-5 animate-spin" />
          Cargando disponibilidad...
        </div>
      )}

      {!loading && error && <p className="text-sm text-red-600 py-6 text-center">{error}</p>}

      {!loading && !error && times.length === 0 && (
        <div className="text-center py-10 text-gray-600">
          <p>No hay horarios disponibles esta semana.</p>
          <button
            type="button"
            onClick={() => setWeekStart(addDaysToDateStr(weekStart, 7))}
            className="mt-2 text-blue-600 font-semibold hover:underline"
          >
            Ver la semana siguiente
          </button>
        </div>
      )}

      {!loading && !error && times.length > 0 && (
        <div className="overflow-auto max-h-[32rem] border border-gray-200 rounded-lg">
          <table className="w-full min-w-[720px] border-collapse text-sm table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-20 p-2 border-b border-gray-200 text-left text-gray-500 font-semibold">Hora</th>
                {dates.map((date) => {
                  const { weekday, day } = formatDayHeader(date);
                  const isToday = date === today;
                  return (
                    <th
                      key={date}
                      className={`p-2 border-b border-l border-gray-200 font-semibold capitalize ${date < today ? 'text-gray-400' : 'text-gray-700'}`}
                    >
                      <span className="block text-xs">{weekday}</span>
                      <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${isToday ? 'bg-blue-600 text-white' : ''}`}>
                        {day}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {times.map((time) => (
                <tr key={time} className="align-top">
                  <th scope="row" className="p-2 border-b border-gray-100 text-left text-gray-500 font-medium whitespace-nowrap">
                    {formatSlotLabel(time)}
                  </th>
                  {dates.map((date) => {
                    const cellKey = `${date} ${time}`;
                    const cellDoctors = doctorsAt(date, time);
                    const expanded = expandedCell === cellKey;
                    const shown = expanded ? cellDoctors : cellDoctors.slice(0, MAX_VISIBLE_PER_CELL);
                    const hidden = cellDoctors.length - shown.length;

                    return (
                      <td key={cellKey} className={`p-1 border-b border-l border-gray-100 ${cellDoctors.length ? 'bg-blue-50/40' : ''}`}>
                        <div className="flex flex-col gap-1">
                          {shown.map((doctor) => (
                            <button
                              key={doctor._id}
                              type="button"
                              onClick={() => onSelectSlot(doctor, date, time)}
                              title={[formatDoctorName(doctor), doctor.specialization].filter(Boolean).join(' · ')}
                              aria-label={`Agendar con ${shortDoctorName(doctor)} el ${formatDayLong(date)} a las ${formatSlotLabel(time)}`}
                              className="w-full truncate text-left px-2 py-1 rounded-md bg-white border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-600 hover:text-white hover:border-blue-600 transition"
                            >
                              {shortDoctorName(doctor)}
                            </button>
                          ))}
                          {hidden > 0 && (
                            <button
                              type="button"
                              onClick={() => setExpandedCell(cellKey)}
                              className="text-xs text-blue-600 font-semibold hover:underline text-left px-2"
                            >
                              +{hidden} más
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default WeeklyAvailabilityCalendar;
